import { getRandomPartialColor } from '#lib/helper/color.js';
import { generateUUID } from '#lib/helper/uuid.js';
import { type TimeslotMinute } from '#lib/helper/openingHour.js';
import { activityStore } from '#lib/stores/activity.js';
import { areaStore } from '#lib/stores/area.js';
import {
	OfferEngineType,
	SessionEngineEventLocationOverrideMode,
	type Area,
	type BookingSession,
	type SaveSessionEngineRequest,
	type Session,
	type SessionEngine,
	type SessionInstance
} from '#lib/types/generated/index.js';
import type { DateTime } from 'luxon';
import type { DropdownOption } from '#lib/types/ui.js';
import { derived, get } from 'svelte/store';

export function getEngineMinCustomerDuration(engine: SessionEngine | null): number {
	if (!engine) return 0;
	if (engine.type === OfferEngineType.Recurrent) {
		if (engine.capacityDurations.length === 0) return 0;
		return (
			Math.min(...engine.capacityDurations.map((duration) => duration.duration)) +
			engine.onboardingDuration +
			engine.debriefingDuration
		);
	} else {
		if (engine.dates.length === 0) return 0;
		return Math.min(
			...engine.dates.map((dateRow) => dateRow.endMinutes - dateRow.startMinutes)
		);
	}
}

export function getEngineMaxCustomerDuration(engine: SessionEngine | null): number {
	if (!engine) return 0;
	if (engine.type === OfferEngineType.Recurrent) {
		if (engine.capacityDurations.length === 0) return 0;
		return (
			Math.max(...engine.capacityDurations.map((duration) => duration.duration)) +
			engine.onboardingDuration +
			engine.debriefingDuration
		);
	} else {
		if (engine.dates.length === 0) return 0;
		return Math.max(
			...engine.dates.map((dateRow) => dateRow.endMinutes - dateRow.startMinutes)
		);
	}
}

interface EventSlotTimeslot {
	startMinutes: number;
	endMinutes: number;
}

export function computeStartTimeslot(interval: number, startTimeslot: number): number {
	const adjustedInterval = Math.min(interval, 15);
	return (
		Math.round(startTimeslot / Math.max(adjustedInterval, 5)) * Math.max(adjustedInterval, 5)
	);
}

interface SessionSlotResolution {
	eventSlot: EventSlotTimeslot | null;
	start: number;
}

export function resolveSessionSlotTime(
	session: Session | undefined | null,
	date: DateTime,
	startTimeslot: number
): SessionSlotResolution {
	const eventSlot = session ? getMatchingEventSlot(session.engine, date, startTimeslot) : null;
	const start = eventSlot
		? eventSlot.startMinutes
		: session
			? computeStartTimeslot(getEngineStepMinutes(session.engine), startTimeslot)
			: startTimeslot;

	return { eventSlot, start };
}

export function getMatchingEventSlot(
	engine: SessionEngine | null,
	date: DateTime,
	targetMinutes?: number
): EventSlotTimeslot | null {
	if (engine?.type !== OfferEngineType.Event || engine.dates.length === 0) return null;
	const datesForDay = engine.dates.filter((d) => d.date.hasSame(date, 'day'));
	if (datesForDay.length === 0) return null;

	if (targetMinutes === undefined) {
		return { startMinutes: datesForDay[0].startMinutes, endMinutes: datesForDay[0].endMinutes };
	}

	const exact = datesForDay.find((d) => d.startMinutes === targetMinutes);
	if (exact) return { startMinutes: exact.startMinutes, endMinutes: exact.endMinutes };

	let closest = datesForDay[0];
	let minDiff = Math.abs(closest.startMinutes - targetMinutes);
	for (let i = 1; i < datesForDay.length; i++) {
		const diff = Math.abs(datesForDay[i].startMinutes - targetMinutes);
		if (diff < minDiff) {
			minDiff = diff;
			closest = datesForDay[i];
		}
	}
	return { startMinutes: closest.startMinutes, endMinutes: closest.endMinutes };
}

export function getEngineDurationForCapacity(
	engine: SessionEngine | null,
	capacity: number,
	date: DateTime,
	startMinutes?: number
): number {
	if (!engine) return 0;
	if (engine.type === OfferEngineType.Recurrent) {
		const duration = engine.capacityDurations.find((d) => {
			return capacity >= d.min && capacity <= d.max;
		});
		return duration ? duration.duration : 0;
	} else {
		if (engine.dates.length === 0) return 0;
		const matching = getMatchingEventSlot(engine, date, startMinutes);
		if (matching) return matching.endMinutes - matching.startMinutes;
		return Math.max(...engine.dates.map((d) => d.endMinutes - d.startMinutes));
	}
}

export function getEngineMinCapacity(engine: SessionEngine | null): number {
	if (!engine) return 1;
	if (engine.type === OfferEngineType.Recurrent) {
		if (engine.capacityDurations.length === 0) return 1;
		return Math.min(...engine.capacityDurations.map((d) => d.min));
	}
	return 1;
}

export function getEngineMaxCapacity(engine: SessionEngine | null): number {
	if (!engine) return Infinity;
	if (engine.type === OfferEngineType.Recurrent) {
		if (engine.capacityDurations.length === 0) return Infinity;
		return Math.max(...engine.capacityDurations.map((d) => d.max));
	} else {
		return engine.totalEventCapacity ?? Infinity;
	}
}

export function getEngineStepMinutes(engine: SessionEngine | null): number {
	if (engine?.type === OfferEngineType.Recurrent) {
		return engine.interval;
	}
	return 5;
}

export function isEngineValid(engine: SessionEngine | null): boolean {
	if (!engine) return false;
	if (engine.type === OfferEngineType.Recurrent) {
		return engine.capacityDurations.length > 0;
	}
	return true;
}

export function getMinCustomerDuration(session: Session): number {
	return getEngineMinCustomerDuration(session.engine);
}

export function getMaxCustomerDuration(session: Session): number {
	return getEngineMaxCustomerDuration(session.engine);
}

export function getSessionDurationForCapacity(
	session: Session,
	capacity: number,
	date: DateTime,
	startMinutes?: number
): number {
	return getEngineDurationForCapacity(session.engine, capacity, date, startMinutes);
}

export function getMinCapacity(session: Session): number {
	return getEngineMinCapacity(session.engine);
}

export const getSessionMaxCapacity = derived([areaStore], ([$areaStore]) => {
	return (session: Session): number => {
		const maxCapacityDuration = getEngineMaxCapacity(session.engine);

		const areas = session.followAllAreas
			? $areaStore.filter((area) => area.parentId === session.parentId)
			: session.areas
					.map((areaId) => $areaStore.find((a) => a.id === areaId))
					.filter((a): a is Area => a !== undefined);

		const maxAreaCapacities = Math.max(0, ...areas.map((area) => area.capacity));

		return Math.min(maxCapacityDuration, maxAreaCapacities);
	};
});

export function createNewRecurrentSession(activityId: string): Session {
	const area = get(areaStore).find((a) => a.parentId === activityId);
	return {
		id: generateUUID(),
		parentId: activityId,
		name: '',
		color: getRandomPartialColor(),
		engine: {
			type: OfferEngineType.Recurrent,
			capacityDurations: [
				{
					min: 1,
					max: area?.capacity ?? 1,
					duration: 30
				}
			],
			preparationDuration: 0,
			onboardingDuration: 0,
			debriefingDuration: 0,
			cleaningDuration: 0,
			interval: 15
		},
		followAllAreas: true,
		areas: [],
		archivedAt: null,
		maxBooking: 0,
		conferenceProvider: null,
		googleConferenceAuthId: null,
		conferenceUrl: null
	};
}

export function createNewEventSession(): Session {
	const availableAreas = get(areaStore).filter((a) => !a.archivedAt);
	const areas = availableAreas.length === 1 ? [availableAreas[0].id] : [];
	const activityId =
		availableAreas.length === 1
			? availableAreas[0].parentId
			: (get(activityStore).filter((a) => !a.archivedAt)[0]?.id ?? '');

	return {
		id: generateUUID(),
		parentId: activityId,
		name: '',
		color: getRandomPartialColor(),
		engine: {
			type: OfferEngineType.Event,
			dates: [],
			totalEventCapacity: null,
			locationOverrideMode: SessionEngineEventLocationOverrideMode.Default,
			customLocation: null,
			customLocationExtra: null
		},
		followAllAreas: false,
		areas,
		archivedAt: null,
		maxBooking: 0,
		conferenceProvider: null,
		googleConferenceAuthId: null,
		conferenceUrl: null
	};
}

export function getAreasForSession(session: Session): Area[] {
	if (session.followAllAreas) {
		return get(areaStore).filter((area) => area.parentId === session.parentId);
	} else {
		return session.areas
			.map((aId) => get(areaStore).find((area) => area.id === aId))
			.filter((a): a is Area => a !== undefined);
	}
}

export function sessionSortFunction(a: Session, b: Session): number {
	return (a.parentId + a.name).localeCompare(b.parentId + b.name);
}

export function toSaveSessionEngineRequest(engine: Session['engine']): SaveSessionEngineRequest {
	if (engine.type === OfferEngineType.Recurrent) {
		return {
			type: engine.type,
			cleaningDuration: engine.cleaningDuration,
			debriefingDuration: engine.debriefingDuration,
			onboardingDuration: engine.onboardingDuration,
			preparationDuration: engine.preparationDuration,
			interval: engine.interval,
			capacityDurations: engine.capacityDurations
		};
	}

	return {
		type: engine.type,
		totalEventCapacity: engine.totalEventCapacity,
		dates: engine.dates.map((d) => ({
			date: d.date.toISODate()!,
			startMinutes: d.startMinutes,
			endMinutes: d.endMinutes
		})),
		locationOverrideMode: engine.locationOverrideMode,
		customLocation: engine.customLocation,
		customLocationExtra: engine.customLocationExtra
	};
}

export function sortSessionInstances(a: SessionInstance, b: SessionInstance): number {
	return a.start - b.start;
}

export function createNewSessionInstance(
	timeslot: TimeslotMinute,
	date: DateTime,
	area: Area,
	sessionId: Session['id']
): SessionInstance {
	return {
		sessionInstanceId: generateUUID(),
		start: timeslot.start,
		end: timeslot.end,
		date,
		sessionId: sessionId,
		areaId: area.id,
		conferenceUrl: null
	};
}

export function createNewBookingSession(sessionId: Session['id'], pax: number): BookingSession {
	return {
		id: generateUUID(),
		sessionInstanceId: null,
		sessionId,
		pax
	};
}

export function getSessionIntervalOptions(
	translate: (key: string, vars?: Record<string, string | number>) => string
): DropdownOption[] {
	return [
		{ key: '5', title: translate('min-long', { min: '5' }), disableTranslate: true },
		{ key: '10', title: translate('min-long', { min: '10' }), disableTranslate: true },
		{ key: '15', title: translate('min-long', { min: '15' }), disableTranslate: true },
		{ key: '20', title: translate('min-long', { min: '20' }), disableTranslate: true },
		{ key: '30', title: translate('min-long', { min: '30' }), disableTranslate: true },
		{ key: '45', title: translate('min-long', { min: '45' }), disableTranslate: true },
		{ key: '60', title: translate('one-hour-long'), disableTranslate: true },
		{ key: '75', title: translate('75min'), disableTranslate: true },
		{ key: '90', title: translate('90min'), disableTranslate: true },
		{ key: '105', title: translate('105min'), disableTranslate: true },
		{ key: '120', title: translate('two-hours-long'), disableTranslate: true }
	];
}
